import React, { useState, useEffect, useCallback } from 'react';
import { formatRomeDate, formatRome } from '../lib/datetime';
import { api, useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  FileText,
  Plus,
  Building2,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Upload,
  Loader2,
  ExternalLink,
  Trash2,
  ChevronDown,
  ChevronUp,
  Shield,
  FileCheck,
  XCircle,
  Pencil,
  Phone,
  Mail,
  UserCircle,
  Wrench,
  X
} from 'lucide-react';

const frequencyLabels = {
  mensile: 'Mensile',
  bimestrale: 'Bimestrale',
  trimestrale: 'Trimestrale',
  quadrimestrale: 'Quadrimestrale',
  semestrale: 'Semestrale',
  annuale: 'Annuale',
  biennale: 'Biennale'
};

const statusConfig = {
  in_scadenza: { label: 'In Scadenza', color: 'bg-amber-100 text-amber-800', icon: Clock },
  completata: { label: 'Completata', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  scaduta: { label: 'Scaduta', color: 'bg-red-100 text-red-800', icon: XCircle }
};

const ContractMaintenancePage = () => {
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hotelFilter, setHotelFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [expandedContracts, setExpandedContracts] = useState({});
  const [saving, setSaving] = useState(false);

  // Create form state
  const [form, setForm] = useState({
    supplier_name: '',
    description: '',
    hotel_id: '',
    frequency: 'semestrale',
    signature_date: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    contact_info: '',
    phone: '',
    email: '',
    contract_number: '',
    alert_days_before: 7,
    service_types: []
  });
  const [newServiceType, setNewServiceType] = useState('');

  // Edit dialog
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editNewServiceType, setEditNewServiceType] = useState('');

  // Add intervention dialog
  const [addInterventionDialog, setAddInterventionDialog] = useState(null);
  const [newInterventionForm, setNewInterventionForm] = useState({ service_type: '', scheduled_date: '' });

  // Confirm form state
  const [confirmForm, setConfirmForm] = useState({ notes: '', file: null, uploading: false });

  const canCreate = ['super_admin', 'sede'].includes(user?.role);
  const canConfirm = ['super_admin', 'hotel_manager', 'sede'].includes(user?.role);
  const canDelete = ['super_admin', 'sede'].includes(user?.role);

  // Delete intervention dialog
  const [deleteInterventionDialog, setDeleteInterventionDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = hotelFilter !== 'all' ? `?hotel_id=${hotelFilter}` : '';
      const [contractsRes, hotelsRes] = await Promise.all([
        api.get(`/contract-maintenance${params}`),
        api.get('/hotels')
      ]);
      setContracts(contractsRes.data);
      setHotels(hotelsRes.data);
    } catch {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [hotelFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.supplier_name || !form.hotel_id || !form.description) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    try {
      setSaving(true);
      await api.post('/contract-maintenance', form);
      toast.success('Contratto creato con successo');
      setShowCreateDialog(false);
      setForm({
        supplier_name: '', description: '', hotel_id: '', frequency: 'semestrale',
        signature_date: '', start_date: new Date().toISOString().split('T')[0], end_date: '',
        contact_info: '', phone: '', email: '', contract_number: '',
        alert_days_before: 7, service_types: []
      });
      setNewServiceType('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (contractId) => {
    if (!window.confirm('Vuoi disattivare questo contratto?')) return;
    try {
      await api.delete(`/contract-maintenance/${contractId}`);
      toast.success('Contratto disattivato');
      fetchData();
    } catch {
      toast.error('Errore');
    }
  };

  const openEdit = (contract) => {
    setEditForm({
      id: contract.id,
      supplier_name: contract.supplier_name || '',
      description: contract.description || '',
      contact_info: contract.contact_info || '',
      phone: contract.phone || '',
      email: contract.email || '',
      contract_number: contract.contract_number || '',
      alert_days_before: contract.alert_days_before || 7,
      end_date: contract.end_date ? new Date(contract.end_date).toISOString().split('T')[0] : '',
      signature_date: contract.signature_date ? new Date(contract.signature_date).toISOString().split('T')[0] : '',
      service_types: contract.service_types || [],
    });
    setEditDialog(contract);
  };

  const handleEdit = async () => {
    try {
      setSaving(true);
      const payload = { ...editForm };
      delete payload.id;
      // Date: invia null se vuoto
      if (!payload.end_date) payload.end_date = null;
      if (!payload.signature_date) payload.signature_date = null;
      await api.put(`/contract-maintenance/${editForm.id}`, payload);
      toast.success('Contratto aggiornato');
      setEditDialog(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmIntervention = async (contractId, occurrenceId, interventionId) => {
    if (!window.confirm("Confermare l'esecuzione di questo intervento?")) return;
    try {
      await api.put(
        `/contract-maintenance/${contractId}/occurrence/${occurrenceId}/intervention/${interventionId}/confirm`,
        {}
      );
      toast.success('Intervento confermato');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const handleAddIntervention = async () => {
    if (!addInterventionDialog) return;
    const { contractId, occurrenceId } = addInterventionDialog;
    if (!newInterventionForm.service_type.trim()) {
      toast.error('Inserisci il tipo di intervento');
      return;
    }
    try {
      await api.post(
        `/contract-maintenance/${contractId}/occurrence/${occurrenceId}/intervention`,
        {
          service_type: newInterventionForm.service_type.trim(),
          scheduled_date: newInterventionForm.scheduled_date || null,
        }
      );
      toast.success('Intervento aggiunto');
      setAddInterventionDialog(null);
      setNewInterventionForm({ service_type: '', scheduled_date: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const handleRescheduleIntervention = async (contractId, occurrenceId, interventionId, newDate) => {
    try {
      await api.put(
        `/contract-maintenance/${contractId}/occurrence/${occurrenceId}/intervention/${interventionId}`,
        { scheduled_date: newDate ? new Date(newDate).toISOString() : null }
      );
      toast.success('Data intervento aggiornata');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteIntervention = async () => {
    if (!deleteInterventionDialog) return;
    const { contractId, occurrenceId, interventionId } = deleteInterventionDialog;
    try {
      setDeleting(true);
      await api.delete(
        `/contract-maintenance/${contractId}/occurrence/${occurrenceId}/intervention/${interventionId}`
      );
      toast.success('Intervento eliminato');
      setDeleteInterventionDialog(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore eliminazione');
    } finally {
      setDeleting(false);
    }
  };

  const handleUploadAndConfirm = async () => {
    if (!confirmDialog) return;
    const { contractId, occurrenceId } = confirmDialog;

    try {
      setSaving(true);
      let attachmentUrl = null;
      let attachmentName = null;

      if (confirmForm.file) {
        setConfirmForm(prev => ({ ...prev, uploading: true }));
        const formData = new FormData();
        formData.append('file', confirmForm.file);
        const uploadRes = await api.post('/upload/document', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        attachmentUrl = uploadRes.data.url;
        attachmentName = uploadRes.data.filename;
      }

      await api.put(`/contract-maintenance/${contractId}/occurrence/${occurrenceId}/confirm`, {
        attachment_url: attachmentUrl,
        attachment_name: attachmentName,
        notes: confirmForm.notes
      });

      toast.success('Manutenzione confermata');
      setConfirmDialog(null);
      setConfirmForm({ notes: '', file: null, uploading: false });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella conferma');
    } finally {
      setSaving(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedContracts(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getHotelName = (hotelId) => {
    const h = hotels.find(h => h.id === hotelId);
    return h?.name || 'N/A';
  };

  const getStats = (contract) => {
    const occs = contract.occurrences || [];
    const total = occs.length;
    const completed = occs.filter(o => o.status === 'completata').length;
    const overdue = occs.filter(o => o.status === 'scaduta').length;
    const upcoming = occs.filter(o => o.status === 'in_scadenza').length;
    return { total, completed, overdue, upcoming };
  };

  const getNextOccurrence = (contract) => {
    const now = new Date();
    const upcoming = (contract.occurrences || [])
      .filter(o => o.status === 'in_scadenza')
      .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));
    return upcoming[0] || null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  // Summary stats
  const totalContracts = contracts.length;
  const totalOverdue = contracts.reduce((s, c) => s + (c.occurrences || []).filter(o => o.status === 'scaduta').length, 0);
  const totalCompleted = contracts.reduce((s, c) => s + (c.occurrences || []).filter(o => o.status === 'completata').length, 0);
  const upcomingSoon = contracts.reduce((s, c) => {
    const now = new Date();
    return s + (c.occurrences || []).filter(o => {
      if (o.status !== 'in_scadenza') return false;
      const d = new Date(o.scheduled_date);
      return (d - now) / 86400000 <= 30;
    }).length;
  }, 0);

  return (
    <div className="space-y-6" data-testid="contract-maintenance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900">Manutenzioni Contrattuali</h1>
          <p className="text-slate-500 mt-1">Registro manutenzioni periodiche da fornitori esterni</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={hotelFilter} onValueChange={setHotelFilter}>
            <SelectTrigger className="w-48" data-testid="contract-hotel-filter">
              <SelectValue placeholder="Tutti gli hotel" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli hotel</SelectItem>
              {hotels.map(h => (
                <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canCreate && (
            <Button className="bg-blue-900 hover:bg-blue-800" onClick={() => setShowCreateDialog(true)} data-testid="create-contract-button">
              <Plus className="w-4 h-4 mr-2" /> Nuovo Contratto
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase font-medium">Contratti Attivi</p>
              <p className="text-2xl font-black text-slate-900">{totalContracts}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-700" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase font-medium">Completate</p>
              <p className="text-2xl font-black text-green-700">{totalCompleted}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-700" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase font-medium">Scadute</p>
              <p className="text-2xl font-black text-red-700">{totalOverdue}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase font-medium">Prossimi 30gg</p>
              <p className="text-2xl font-black text-amber-700">{upcomingSoon}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Contracts List */}
      {contracts.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Nessun contratto di manutenzione registrato</p>
            {canCreate && (
              <Button className="mt-4 bg-blue-900 hover:bg-blue-800" onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" /> Crea il primo contratto
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {contracts.map(contract => {
            const stats = getStats(contract);
            const nextOcc = getNextOccurrence(contract);
            const isExpanded = expandedContracts[contract.id];
            const sortedOccs = [...(contract.occurrences || [])].sort(
              (a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date)
            );

            return (
              <Card key={contract.id} className="border-slate-200" data-testid={`contract-card-${contract.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="w-10 h-10 bg-blue-900 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                        <FileCheck className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg font-bold">{contract.supplier_name}</CardTitle>
                        <p className="text-sm text-slate-500 mt-0.5">{contract.description}</p>
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3.5 h-3.5" /> {getHotelName(contract.hotel_id)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" /> {frequencyLabels[contract.frequency]}
                          </span>
                          {contract.contract_number && (
                            <span className="flex items-center gap-1">
                              <FileText className="w-3.5 h-3.5" /> #{contract.contract_number}
                            </span>
                          )}
                          {contract.end_date && (
                            <span className="flex items-center gap-1 text-amber-700">
                              <Clock className="w-3.5 h-3.5" /> Scade {formatRomeDate(contract.end_date)}
                            </span>
                          )}
                          {contract.signature_date && (
                            <span className="flex items-center gap-1 text-slate-500" data-testid={`contract-signature-${contract.id}`}>
                              <FileText className="w-3.5 h-3.5" /> Stipulato {formatRomeDate(contract.signature_date)}
                            </span>
                          )}
                        </div>
                        {/* Recapiti cliccabili */}
                        {(contract.phone || contract.email || contract.contact_info) && (
                          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs" data-testid={`contract-contacts-${contract.id}`}>
                            {contract.phone && (
                              <a
                                href={`tel:${contract.phone}`}
                                className="flex items-center gap-1 bg-blue-50 hover:bg-blue-100 text-blue-700 px-2 py-1 rounded-md font-medium"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Phone className="w-3.5 h-3.5" /> {contract.phone}
                              </a>
                            )}
                            {contract.email && (
                              <a
                                href={`mailto:${contract.email}`}
                                className="flex items-center gap-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-2 py-1 rounded-md font-medium"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Mail className="w-3.5 h-3.5" /> {contract.email}
                              </a>
                            )}
                            {contract.contact_info && (
                              <span className="flex items-center gap-1 text-slate-600 bg-slate-100 px-2 py-1 rounded-md" data-testid={`contract-contact-info-${contract.id}`}>
                                <UserCircle className="w-3.5 h-3.5" /> {contract.contact_info}
                              </span>
                            )}
                          </div>
                        )}
                        {/* Service types (interventi previsti) */}
                        {(contract.service_types || []).length > 0 && (
                          <div className="flex flex-wrap items-center gap-1 mt-2" data-testid={`contract-services-${contract.id}`}>
                            <span className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mr-1">Interventi previsti:</span>
                            {contract.service_types.map((st, i) => (
                              <Badge key={i} variant="outline" className="text-[10px] bg-amber-50 border-amber-200 text-amber-800">
                                <Wrench className="w-2.5 h-2.5 mr-1" />{st}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {stats.overdue > 0 && (
                        <Badge className="bg-red-100 text-red-700 font-bold">{stats.overdue} scadute</Badge>
                      )}
                      {nextOcc && (
                        <Badge className="bg-amber-50 text-amber-700 font-medium">
                          Prossima: {formatRomeDate(nextOcc.scheduled_date)}
                        </Badge>
                      )}
                      <Badge variant="secondary" className="font-medium">
                        {stats.completed}/{stats.total}
                      </Badge>
                      {canCreate && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-blue-500 hover:text-blue-700"
                            onClick={() => openEdit(contract)}
                            data-testid={`edit-contract-${contract.id}`}
                            title="Modifica contratto"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600" onClick={() => handleDelete(contract.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {/* Progress bar */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full transition-all"
                        style={{ width: `${stats.total > 0 ? (stats.completed / stats.total) * 100 : 0}%` }}
                      />
                    </div>
                    <span className="text-xs text-slate-500 font-medium">{stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}%</span>
                  </div>

                  {/* Expand/Collapse */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-slate-500 hover:text-slate-700"
                    onClick={() => toggleExpand(contract.id)}
                    data-testid={`expand-contract-${contract.id}`}
                  >
                    {isExpanded ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                    {isExpanded ? 'Nascondi scadenze' : `Mostra ${stats.total} scadenze`}
                  </Button>

                  {isExpanded && (
                    <div className="mt-3 border-t border-slate-100 pt-3">
                      <div className="max-h-[500px] overflow-y-auto pr-2" data-testid={`occurrences-scroll-${contract.id}`}>
                        <div className="space-y-2">
                          {sortedOccs.map(occ => {
                            const cfg = statusConfig[occ.status] || statusConfig.in_scadenza;
                            const StatusIcon = cfg.icon;
                            const schedDate = new Date(occ.scheduled_date);
                            const isPast = schedDate < new Date();

                            return (
                              <div
                                key={occ.id}
                                className={`p-3 rounded-lg border ${
                                  occ.status === 'scaduta' ? 'border-red-200 bg-red-50/50' :
                                  occ.status === 'completata' ? 'border-green-200 bg-green-50/50' :
                                  'border-slate-200 bg-white'
                                }`}
                                data-testid={`occurrence-${occ.id}`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <StatusIcon className={`w-5 h-5 ${
                                      occ.status === 'completata' ? 'text-green-600' :
                                      occ.status === 'scaduta' ? 'text-red-600' :
                                      'text-amber-600'
                                    }`} />
                                    <div>
                                      <p className="text-sm font-medium text-slate-900">
                                        {formatRome(schedDate, { day: 'numeric', month: 'long', year: 'numeric' })}
                                      </p>
                                      {occ.status === 'completata' && occ.confirmed_at && (
                                        <p className="text-xs text-green-600 mt-0.5">
                                          Confermata il {formatRomeDate(occ.confirmed_at)}
                                          {occ.notes && ` — ${occ.notes}`}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge className={`${cfg.color} text-xs`}>{cfg.label}</Badge>
                                    {occ.status === 'completata' && occ.attachment_url && (
                                      <a
                                        href={occ.attachment_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                                        data-testid={`download-attachment-${occ.id}`}
                                      >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                        {occ.attachment_name || 'Rapportino'}
                                      </a>
                                    )}
                                    {occ.status !== 'completata' && canConfirm && (!(occ.interventions && occ.interventions.length > 0)) && (
                                      <Button
                                        size="sm"
                                        className="bg-green-600 hover:bg-green-700 text-xs h-8"
                                        onClick={() => {
                                          setConfirmDialog({ contractId: contract.id, occurrenceId: occ.id, date: schedDate });
                                          setConfirmForm({ notes: '', file: null, uploading: false });
                                        }}
                                        data-testid={`confirm-btn-${occ.id}`}
                                      >
                                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Conferma
                                      </Button>
                                    )}
                                  </div>
                                </div>

                                {/* Lista interventi (se previsti) */}
                                {(occ.interventions && occ.interventions.length > 0) && (
                                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-2" data-testid={`interventions-${occ.id}`}>
                                    {occ.interventions.map((intv) => {
                                      const intvDate = intv.scheduled_date ? new Date(intv.scheduled_date) : schedDate;
                                      const isCustom = !!intv.scheduled_date;
                                      return (
                                        <div
                                          key={intv.id}
                                          className={`flex items-center justify-between gap-2 px-3 py-2 rounded ${intv.completed ? 'bg-green-50/60' : 'bg-slate-50'}`}
                                          data-testid={`intervention-${intv.id}`}
                                        >
                                          <div className="flex items-center gap-2 flex-1 min-w-0">
                                            {intv.completed ? (
                                              <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                                            ) : (
                                              <Wrench className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                              <div className={`text-sm font-medium ${intv.completed ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                                                {intv.service_type}
                                              </div>
                                              <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
                                                <span className={isCustom ? 'text-blue-600 font-semibold' : ''}>
                                                  {isCustom ? '📅 Data custom: ' : ''}{formatRomeDate(intvDate)}
                                                </span>
                                                {intv.completed && intv.completed_by_name && (
                                                  <span className="text-green-700">· {intv.completed_by_name}</span>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-2 flex-shrink-0">
                                            {!intv.completed && canConfirm && (
                                              <>
                                                <Input
                                                  type="date"
                                                  className="h-8 text-xs w-36 flex-shrink-0"
                                                  value={intv.scheduled_date ? new Date(intv.scheduled_date).toISOString().split('T')[0] : ''}
                                                  onChange={(e) => handleRescheduleIntervention(contract.id, occ.id, intv.id, e.target.value)}
                                                  title="Sposta a una data specifica"
                                                  data-testid={`reschedule-${intv.id}`}
                                                />
                                                <Button
                                                  size="sm"
                                                  className="bg-green-600 hover:bg-green-700 text-xs h-8 flex-shrink-0"
                                                  onClick={() => handleConfirmIntervention(contract.id, occ.id, intv.id)}
                                                  data-testid={`confirm-int-${intv.id}`}
                                                >
                                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Fatto
                                                </Button>
                                              </>
                                            )}
                                            {canDelete && (
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                                                onClick={() => setDeleteInterventionDialog({
                                                  contractId: contract.id,
                                                  occurrenceId: occ.id,
                                                  interventionId: intv.id,
                                                  serviceType: intv.service_type,
                                                  isCompleted: intv.completed,
                                                  isCustom,
                                                  scheduledDate: intvDate,
                                                })}
                                                title="Elimina intervento"
                                                data-testid={`delete-int-${intv.id}`}
                                              >
                                                <X className="w-4 h-4" />
                                              </Button>
                                            )}
                                            {intv.completed && intv.completed_at && (
                                              <span className="text-[10px] text-green-700">
                                                {formatRomeDate(intv.completed_at)}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                    {canCreate && occ.status !== 'completata' && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="w-full text-xs h-7 mt-1"
                                        onClick={() => {
                                          setAddInterventionDialog({ contractId: contract.id, occurrenceId: occ.id });
                                          setNewInterventionForm({ service_type: '', scheduled_date: '' });
                                        }}
                                        data-testid={`add-intervention-${occ.id}`}
                                      >
                                        <Plus className="w-3 h-3 mr-1" /> Aggiungi intervento extra
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Contract Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo Contratto Manutenzione</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Fornitore *</label>
              <Input
                placeholder="es. Aspire, Otis, Schindler"
                value={form.supplier_name}
                onChange={e => setForm({ ...form, supplier_name: e.target.value })}
                data-testid="input-supplier"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Descrizione intervento *</label>
              <Textarea
                placeholder="es. Manutenzione impianto aspirazione centralizzato"
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                data-testid="input-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Hotel *</label>
                <Select value={form.hotel_id || '_none'} onValueChange={v => setForm({ ...form, hotel_id: v === '_none' ? '' : v })}>
                  <SelectTrigger data-testid="select-hotel">
                    <SelectValue placeholder="Seleziona hotel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">-- Seleziona --</SelectItem>
                    {hotels.map(h => (
                      <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Frequenza *</label>
                <Select value={form.frequency} onValueChange={v => setForm({ ...form, frequency: v })}>
                  <SelectTrigger data-testid="select-frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mensile">Mensile</SelectItem>
                    <SelectItem value="bimestrale">Bimestrale</SelectItem>
                    <SelectItem value="trimestrale">Trimestrale</SelectItem>
                    <SelectItem value="quadrimestrale">Quadrimestrale</SelectItem>
                    <SelectItem value="semestrale">Semestrale</SelectItem>
                    <SelectItem value="annuale">Annuale</SelectItem>
                    <SelectItem value="biennale">Biennale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Data stipula contratto</label>
                <Input
                  type="date"
                  value={form.signature_date}
                  onChange={e => setForm({ ...form, signature_date: e.target.value })}
                  data-testid="input-signature-date"
                />
                <p className="text-[10px] text-slate-500">Solo informativa</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Data inizio scadenze *</label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={e => setForm({ ...form, start_date: e.target.value })}
                  data-testid="input-start-date"
                />
                <p className="text-[10px] text-slate-500">Prima manutenzione</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Data fine</label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={e => setForm({ ...form, end_date: e.target.value })}
                  data-testid="input-end-date"
                />
                <p className="text-[10px] text-slate-500">Limite generazione</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Nr. Contratto</label>
              <Input
                placeholder="es. CTR-2026-001"
                value={form.contract_number}
                onChange={e => setForm({ ...form, contract_number: e.target.value })}
                data-testid="input-contract-number"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> Telefono fornitore</label>
                <Input
                  placeholder="es. +39 02 1234567"
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  data-testid="input-phone"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email fornitore</label>
                <Input
                  type="email"
                  placeholder="es. info@fornitore.it"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  data-testid="input-email"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Note contatto aggiuntive</label>
                <Input
                  placeholder="es. Sig. Rossi, referente tecnico"
                  value={form.contact_info}
                  onChange={e => setForm({ ...form, contact_info: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Alert (giorni prima)</label>
                <Input
                  type="number"
                  min={1}
                  max={60}
                  value={form.alert_days_before}
                  onChange={e => setForm({ ...form, alert_days_before: parseInt(e.target.value) || 7 })}
                />
              </div>
            </div>
            {/* Service types */}
            <div className="space-y-2 border border-amber-200 bg-amber-50/40 rounded-lg p-3">
              <label className="text-sm font-semibold flex items-center gap-1 text-amber-900">
                <Wrench className="w-4 h-4" /> Tipi di intervento eseguiti
              </label>
              <p className="text-[11px] text-slate-600">
                Es. Aspire viene semestralmente ma fa 3 interventi distinti (estintori, centralina, gruppo pompe).
                Ogni intervento potrà avere una propria data e conferma indipendente.
              </p>
              <div className="flex gap-2">
                <Input
                  placeholder="Aggiungi tipo di intervento..."
                  value={newServiceType}
                  onChange={e => setNewServiceType(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const v = newServiceType.trim();
                      if (v && !form.service_types.includes(v)) {
                        setForm({ ...form, service_types: [...form.service_types, v] });
                        setNewServiceType('');
                      }
                    }
                  }}
                  data-testid="input-new-service-type"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const v = newServiceType.trim();
                    if (v && !form.service_types.includes(v)) {
                      setForm({ ...form, service_types: [...form.service_types, v] });
                      setNewServiceType('');
                    }
                  }}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {form.service_types.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {form.service_types.map((st, i) => (
                    <Badge key={i} className="bg-amber-100 text-amber-800 border border-amber-300 pr-1 flex items-center gap-1">
                      {st}
                      <button
                        type="button"
                        className="ml-1 hover:bg-amber-200 rounded p-0.5"
                        onClick={() => setForm({ ...form, service_types: form.service_types.filter((_, idx) => idx !== i) })}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Annulla</Button>
            <Button className="bg-blue-900 hover:bg-blue-800" onClick={handleCreate} disabled={saving} data-testid="save-contract-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Crea Contratto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Occurrence Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={(open) => { if (!open) setConfirmDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Conferma Manutenzione</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {confirmDialog && (
              <p className="text-sm text-slate-600">
                Conferma l&apos;avvenuta manutenzione programmata per il{' '}
                <strong>{formatRome(confirmDialog.date, { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
              </p>
            )}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Rapportino / Documento</label>
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                {confirmForm.file ? (
                  <div className="flex items-center justify-center gap-2 text-sm text-green-700">
                    <FileCheck className="w-4 h-4" />
                    <span>{confirmForm.file.name}</span>
                    <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => setConfirmForm(prev => ({ ...prev, file: null }))}>
                      <XCircle className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <Upload className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                    <p className="text-sm text-slate-500">Clicca per caricare (PDF, JPG, PNG)</p>
                    <p className="text-xs text-slate-400">Max 10MB</p>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={e => {
                        if (e.target.files[0]) setConfirmForm(prev => ({ ...prev, file: e.target.files[0] }));
                      }}
                      data-testid="upload-attachment"
                    />
                  </label>
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Note</label>
              <Textarea
                placeholder="es. Intervento completato regolarmente, nessuna anomalia"
                value={confirmForm.notes}
                onChange={e => setConfirmForm(prev => ({ ...prev, notes: e.target.value }))}
                data-testid="confirm-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(null)}>Annulla</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleUploadAndConfirm} disabled={saving} data-testid="confirm-maintenance-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Conferma Avvenuta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Contract Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(o) => !o && setEditDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-600" /> Modifica Contratto
            </DialogTitle>
            <DialogDescription>Aggiorna dati fornitore, recapiti, scadenza e tipi di intervento</DialogDescription>
          </DialogHeader>
          {editDialog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Fornitore</label>
                  <Input value={editForm.supplier_name || ''} onChange={e => setEditForm({ ...editForm, supplier_name: e.target.value })} data-testid="edit-supplier-name" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Nr. Contratto</label>
                  <Input value={editForm.contract_number || ''} onChange={e => setEditForm({ ...editForm, contract_number: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Descrizione</label>
                <Textarea value={editForm.description || ''} onChange={e => setEditForm({ ...editForm, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> Telefono</label>
                  <Input value={editForm.phone || ''} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} data-testid="edit-phone" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email</label>
                  <Input type="email" value={editForm.email || ''} onChange={e => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-email" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Note contatto</label>
                  <Input value={editForm.contact_info || ''} onChange={e => setEditForm({ ...editForm, contact_info: e.target.value })} data-testid="edit-contact-info" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Data fine contratto</label>
                  <Input type="date" value={editForm.end_date || ''} onChange={e => setEditForm({ ...editForm, end_date: e.target.value })} data-testid="edit-end-date" />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Data stipula contratto <span className="text-slate-400 font-normal">(informativa)</span></label>
                <Input type="date" value={editForm.signature_date || ''} onChange={e => setEditForm({ ...editForm, signature_date: e.target.value })} data-testid="edit-signature-date" />
              </div>
              <div className="space-y-2 border border-amber-200 bg-amber-50/40 rounded-lg p-3">
                <label className="text-sm font-semibold flex items-center gap-1 text-amber-900">
                  <Wrench className="w-4 h-4" /> Tipi di intervento
                </label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Nuovo intervento..."
                    value={editNewServiceType}
                    onChange={e => setEditNewServiceType(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const v = editNewServiceType.trim();
                        if (v && !(editForm.service_types || []).includes(v)) {
                          setEditForm({ ...editForm, service_types: [...(editForm.service_types || []), v] });
                          setEditNewServiceType('');
                        }
                      }
                    }}
                    data-testid="edit-input-new-service-type"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const v = editNewServiceType.trim();
                      if (v && !(editForm.service_types || []).includes(v)) {
                        setEditForm({ ...editForm, service_types: [...(editForm.service_types || []), v] });
                        setEditNewServiceType('');
                      }
                    }}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {(editForm.service_types || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {(editForm.service_types || []).map((st, i) => (
                      <Badge key={i} className="bg-amber-100 text-amber-800 border border-amber-300 pr-1 flex items-center gap-1">
                        {st}
                        <button
                          type="button"
                          className="ml-1 hover:bg-amber-200 rounded p-0.5"
                          onClick={() => setEditForm({ ...editForm, service_types: editForm.service_types.filter((_, idx) => idx !== i) })}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-[11px] text-slate-500">
                  ⚠ Aggiungere/rimuovere tipi aggiornerà anche le occurrence future ancora aperte.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)}>Annulla</Button>
            <Button className="bg-blue-900 hover:bg-blue-800" onClick={handleEdit} disabled={saving} data-testid="save-edit-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Salva Modifiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Intervention Dialog */}
      <Dialog open={!!addInterventionDialog} onOpenChange={(o) => !o && setAddInterventionDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-600" /> Aggiungi intervento extra
            </DialogTitle>
            <DialogDescription>Aggiunge un intervento non previsto a questa visita</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Tipo di intervento *</label>
              <Input
                placeholder="es. Sostituzione filtri"
                value={newInterventionForm.service_type}
                onChange={e => setNewInterventionForm({ ...newInterventionForm, service_type: e.target.value })}
                data-testid="new-intervention-service-type"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Data specifica (opzionale)</label>
              <Input
                type="date"
                value={newInterventionForm.scheduled_date}
                onChange={e => setNewInterventionForm({ ...newInterventionForm, scheduled_date: e.target.value })}
                data-testid="new-intervention-date"
              />
              <p className="text-[11px] text-slate-500">Se vuoto, usa la data della visita principale.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddInterventionDialog(null)}>Annulla</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={handleAddIntervention} data-testid="save-new-intervention">
              <Plus className="w-4 h-4 mr-2" /> Aggiungi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Intervention Confirmation Dialog */}
      <Dialog open={!!deleteInterventionDialog} onOpenChange={(o) => !o && !deleting && setDeleteInterventionDialog(null)}>
        <DialogContent data-testid="delete-intervention-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Eliminare questo intervento?
            </DialogTitle>
            <DialogDescription className="pt-2">
              Stai per eliminare definitivamente l&apos;intervento. Questa operazione <b>non può essere annullata</b>.
            </DialogDescription>
          </DialogHeader>
          {deleteInterventionDialog && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-2">
              <div className="text-sm text-slate-900">
                <span className="font-semibold">Intervento:</span> {deleteInterventionDialog.serviceType}
              </div>
              <div className="text-sm text-slate-700">
                <span className="font-semibold">Data:</span> {formatRomeDate(deleteInterventionDialog.scheduledDate)}
                {deleteInterventionDialog.isCustom && (
                  <Badge className="ml-2 bg-blue-100 text-blue-800 text-[10px]">Data custom</Badge>
                )}
                {deleteInterventionDialog.isCompleted && (
                  <Badge className="ml-2 bg-green-100 text-green-800 text-[10px]">Completato</Badge>
                )}
              </div>
              {deleteInterventionDialog.isCompleted && (
                <p className="text-xs text-red-700 pt-1">
                  ⚠️ Attenzione: questo intervento risulta già completato. Eliminandolo si perde lo storico associato.
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              disabled={deleting}
              onClick={() => setDeleteInterventionDialog(null)}
              data-testid="cancel-delete-intervention"
            >
              Annulla
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
              onClick={handleDeleteIntervention}
              data-testid="confirm-delete-intervention"
            >
              {deleting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Eliminazione...</>) : (<><Trash2 className="w-4 h-4 mr-2" /> Elimina definitivamente</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractMaintenancePage;
